/**
 * Utilities for managing shared props that are loaded once per page
 * and referenced by React components to reduce redundant data.
 */

/**
 * Inflates component data by merging in shared props from window.SHARED_PROPS
 * based on shared_refs configuration or __ref markers in the data.
 * 
 * @param {Object} data - Component data that may contain shared_refs or __ref markers
 * @returns {Object} - Data with shared props merged in
 */
export function inflateShared(data) {
  try {
    const shared = window.SHARED_PROPS;
    if (!shared || !data) return data;

    // Always ensure critical shared props are available if they don't exist
    // This provides backward compatibility for components that expect these props
    if (!data.globals && shared.globals) {
      data.globals = shared.globals;
    }
    if (!data.shop && shared.shop) {
      data.shop = shared.shop;
    }
    if (!data.routes && shared.routes) {
      data.routes = shared.routes;
    }
    if (!data.page && shared.page) {
      data.page = shared.page;
    }
    if (!data.request && shared.request) {
      data.request = shared.request;
    }
    if (!data.localization && shared.localization) {
      data.localization = shared.localization;
    }
    if (!data.card_types && shared.card_types) {
      data.card_types = shared.card_types;
    }
    if (!data.gift_wrap_types && shared.gift_wrap_types) {
      data.gift_wrap_types = shared.gift_wrap_types;
    }
    if (!data.shipping_cost_id && shared.shipping_cost_id) {
      data.shipping_cost_id = shared.shipping_cost_id;
    }

    // Handle shared_refs configuration (for explicit control)
    if (data.shared_refs) {
      if (data.shared_refs.globals && (!data.globals || data.globals.__ref)) {
        data.globals = shared.globals;
      }
      if (data.shared_refs.shop && (!data.shop || data.shop.__ref)) {
        data.shop = shared.shop;
      }
      if (data.shared_refs.routes && (!data.routes || data.routes.__ref)) {
        data.routes = shared.routes;
      }
      if (data.shared_refs.template && (!data.template || data.template.__ref)) {
        data.template = shared.template;
      }
      if (data.shared_refs.localization && (!data.localization || data.localization.__ref)) {
        data.localization = shared.localization;
      }
      if (data.shared_refs.page && (!data.page || data.page.__ref)) {
        data.page = shared.page;
      }
      if (data.shared_refs.request && (!data.request || data.request.__ref)) {
        data.request = shared.request;
      }
      if (data.shared_refs.color_schemas && (!data.color_schemas || data.color_schemas.__ref) && shared.color_schemas) {
        data.color_schemas = shared.color_schemas;
      }
      if (data.shared_refs.card_types && (!data.card_types || data.card_types.__ref) && shared.card_types) {
        data.card_types = shared.card_types;
      }
      if (data.shared_refs.gift_wrap_types && (!data.gift_wrap_types || data.gift_wrap_types.__ref) && shared.gift_wrap_types) {
        data.gift_wrap_types = shared.gift_wrap_types;
      }
      if (data.shared_refs.shipping_cost_id && (!data.shipping_cost_id || data.shipping_cost_id.__ref) && shared.shipping_cost_id) {
        data.shipping_cost_id = shared.shipping_cost_id;
      }
    } 
    // Handle legacy __ref marker for globals
    else if (data.globals && data.globals.__ref) {
      data.globals = shared.globals;
    }

    return data;
  } catch (e) {
    console.warn('inflateShared failed', e);
    return data;
  }
}